import { Box, Button, Select, MenuItem } from "@mui/material";

// CustomPagination.tsx
export const CustomPagination = ({
  totalCount,
  rowsPerPage,
  page,
  onPageChange,
  onRowsPerPageChange,
}: {
  totalCount: any;
  rowsPerPage: any;
  page: any;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
}) => {
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const firstItem = page * rowsPerPage + 1;
  const lastItem = Math.min((page + 1) * rowsPerPage, totalCount);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      onPageChange(newPage);
    }
  };

  let startPage = Math.max(0, page - 2);
  let endPage = Math.min(totalPages - 1, startPage + 4);
  if (endPage - startPage < 4) {
    startPage = Math.max(0, endPage - 4);
  }

  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "stretch", sm: "center" }}
      justifyContent="space-between"
      rowGap={2}
      p={2}
      flexWrap="wrap"
    >
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        flexWrap="wrap"
        justifyContent={{ xs: "center", sm: "flex-start" }}
      >
        <Button
          variant="outlined"
          disabled={page === 0}
          onClick={() => handlePageChange(page - 1)}
          sx={{
            width: 77,
            minWidth: 77,
            minHeight: "36px",
            fontSize: "12px",
            fontWeight: 500,
            padding: 0,
          }}
        >
          &lt; Back
        </Button>

        {startPage > 0 && (
          <>
            <Button
              variant="outlined"
              onClick={() => handlePageChange(0)}
              sx={{ width: 33, minWidth: 33, minHeight: "36px", padding: 0 }}
            >
              1
            </Button>
            {startPage > 1 && <span>...</span>}
          </>
        )}

        {Array.from({ length: endPage - startPage + 1 }, (_, i) => (
          <Button
            key={startPage + i}
            variant={startPage + i === page ? "contained" : "outlined"}
            onClick={() => handlePageChange(startPage + i)}
            sx={{
              width: 33,
              minWidth: 33,
              minHeight: "36px",
              padding: 0,
              bgcolor: startPage + i === page ? "#0055A8" : "transparent",
              color: startPage + i === page ? "white" : "primary.main",
              fontWeight: startPage + i === page ? "bold" : "normal",
              "&:hover": {
                bgcolor:
                  startPage + i === page ? "primary.dark" : "action.hover",
              },
            }}
          >
            {startPage + i + 1}
          </Button>
        ))}

        {endPage < totalPages - 1 && (
          <>
            {endPage < totalPages - 2 && <span>...</span>}
            <Button
              variant="outlined"
              onClick={() => handlePageChange(totalPages - 1)}
              sx={{ width: 33, minWidth: 33, minHeight: "36px", padding: 0 }}
            >
              {totalPages}
            </Button>
          </>
        )}

        <Button
          variant="outlined"
          disabled={page >= totalPages - 1}
          onClick={() => handlePageChange(page + 1)}
          sx={{
            width: 77,
            minWidth: 77,
            minHeight: "36px",
            fontSize: "12px",
            fontWeight: 500,
            padding: 0,
          }}
        >
          Next &gt;
        </Button>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
        justifyContent={{ xs: "center", sm: "flex-end" }}
        mt={{ xs: 2, sm: 0 }}
      >
        <span>Rows per page</span>
        <Select
          value={rowsPerPage}
          onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
          size="small"
        >
          {[10, 50, 100, 150, 200].map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
        <span>
          {firstItem}-{lastItem} of {totalCount}
        </span>
      </Box>
    </Box>
  );
};
